/**
 * Pricing structures for analysis. This module does not price live quotes.
 *
 * PS1 v1 is the quote engine as it works today (`computeTotals` plus the
 * profit figure on the quote screen), frozen on 5 Oct 2026. Do not edit
 * `ps1Totals` to follow later changes in the quote wizard. A change to live
 * pricing is a new structure version. Issued quotes keep the version that
 * produced them.
 *
 * PS2 v0 restates that same customer price in normalised buckets. It does
 * not generate a new price. PS3 v0 is a market/mood experiment on top of PS2.
 */

export const PS1_VERSION = 'ps1.v1' as const;
export const PS2_VERSION = 'ps2.v0-draft' as const;
export const PS3_VERSION = 'ps3.v0-experimental' as const;

export type StructureId = 'ps1' | 'ps2' | 'ps3';
export type StructureVersion = typeof PS1_VERSION | typeof PS2_VERSION | typeof PS3_VERSION;

export type LineKind = 'material' | 'equipment' | 'labour' | 'other';

/** Where a stored line sits once a structure interprets it. */
export type CostBucket =
  | 'goods'
  | 'company_flat_rate'
  | 'direct_fitter'
  | 'additional_trade'
  | 'other_labour';

export const BUCKET_LABELS: Record<CostBucket, string> = {
  goods: 'Goods / Outlay',
  company_flat_rate: 'Company Flat Rate',
  direct_fitter: 'Direct fitter',
  additional_trade: 'Additional trade',
  other_labour: 'Other labour',
};

export type JobLine = {
  id: string;
  lineKind: LineKind | string;
  name: string;
  description?: string | null;
  qty: number;
  unit?: string | null;
  unitPrice: number;
  /** Stored line total. Null means qty × unit price, matching the quote engine. */
  lineTotal: number | null;
  vatInclusive: boolean;
  labourDays?: number | null;
  labourHours?: number | null;
  sortOrder?: number | null;
};

/**
 * The job's own inputs. Pricing structures read these. They do not replace them.
 * Admin, contingency and profit percentages are the PS1 parameters saved on the quote.
 */
export type JobInputs = {
  lines: JobLine[];
  adminPct: number;
  adminCap: number;
  contingencyPct: number;
  profitPct: number;
  vatEnabled: boolean;
  vatPct: number;
  hasDeposit: boolean;
  depositAmount: number;
  /** 0..1 when the deposit is a percentage of the VAT-inclusive total. Null means a fixed amount. */
  depositPercentage: number | null;
  projectDays: number | null;
  projectHours: number | null;
};

export type PricingResult = {
  structureId: StructureId;
  version: StructureVersion;
  label: string;
  customerPriceExVat: number;
  customerPriceIncVat: number;
  goodsOutlay: number;
  directLabour: number;
  companyOverhead: number;
  contingency: number;
  grossProfit: number;
  /** Share of the ex-VAT customer price. Null when that price is zero. */
  grossMargin: number | null;
  netJobProfit: number;
  netJobMargin: number | null;
  keystoneDays: number | null;
  profitPerKeystoneDay: number | null;
  /** The quote screen's "Profit (- all overheads)". PS1 only. */
  quoteScreenProfit: number | null;
  /** The quote screen's profit per day, using its own day rule. PS1 only. */
  quoteScreenProfitPerDay: number | null;
  customerDeposit: number;
  supplierFundingRequirement: number;
  /** Deposit minus supplier funding. Negative is a shortfall. */
  fundingSurplus: number;
  notes: string[];
};

export type Ps1Trace = {
  goodsNet: number;
  labourNet: number;
  flatRateInsideLabour: number;
  baseCost: number;
  adminUncapped: number;
  adminFee: number;
  adminCapped: boolean;
  contingency: number;
  profitMargin: number;
  customerPriceExVat: number;
  vatAmount: number;
  customerPriceIncVat: number;
  quoteScreenProfit: number;
  quoteScreenDayDivisor: number | null;
  quoteScreenProfitPerDay: number | null;
};

export type KeystoneDays = {
  days: number | null;
  source: string;
};

export type LineReading = {
  line: JobLine;
  net: number;
  /** How PS1 prices it: by the stored line kind. */
  ps1Bucket: 'goods' | 'direct_labour' | 'ignored';
  /** How PS2/PS3 read it. Classification overrides apply here only. */
  analysisBucket: CostBucket;
};

export type StructureComparison = {
  ps1: PricingResult;
  ps2: PricingResult;
  ps3: PricingResult;
  trace: Ps1Trace;
  keystone: KeystoneDays;
  lines: LineReading[];
  mood: number;
};

export type StoredQuoteTotals = {
  net: number | null;
  gross: number | null;
  deposit: number | null;
};

export type ReproductionStatus = 'match' | 'differs' | 'no-stored-total' | 'inactive-version';

export type ReproductionCheck = {
  status: ReproductionStatus;
  netDelta: number | null;
  grossDelta: number | null;
  depositDelta: number | null;
};

const GOODS_KINDS = new Set(['material', 'equipment', 'other']);

/** Same half-up rounding the quote engine uses. */
function round2(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

function lineGross(line: JobLine): number {
  return line.lineTotal != null ? line.lineTotal : (line.qty || 0) * (line.unitPrice || 0);
}

/** Net amount, matching `computeTotals` line by line. */
export function lineNet(line: JobLine, vatEnabled: boolean, vatPct: number): number {
  const gross = lineGross(line);
  if (line.vatInclusive && vatEnabled) return round2(gross / (1 + vatPct));
  return round2(gross);
}

/** Cash leaving the business for a goods line, including VAT charged by the supplier. */
function lineSupplierCash(line: JobLine, vatEnabled: boolean, vatPct: number): number {
  const gross = lineGross(line);
  if (line.vatInclusive || !vatEnabled) return round2(gross);
  return round2(round2(gross) * (1 + vatPct));
}

function sum(values: number[]): number {
  return round2(values.reduce((total, value) => total + value, 0));
}

export function defaultBucket(line: JobLine): CostBucket {
  const name = line.name.trim().toLowerCase();
  if (/company\s*flat\s*rate/.test(name) || name === 'flat rate') return 'company_flat_rate';
  if (line.lineKind !== 'labour') return 'goods';
  if (/fitter|labourer|installer/.test(name)) return 'direct_fitter';
  if (/plaster|electric|brick|joiner|carpenter|roof|decorator|painter|builder|scaffold|groundwork|locksmith|tiler|plumber|render|landscap/.test(name)) {
    return 'additional_trade';
  }
  return 'other_labour';
}

export function analysisBucket(line: JobLine, overrides: Record<string, CostBucket> = {}): CostBucket {
  return overrides[line.id] ?? defaultBucket(line);
}

function ps1Bucket(line: JobLine): LineReading['ps1Bucket'] {
  if (line.lineKind === 'labour') return 'direct_labour';
  if (GOODS_KINDS.has(line.lineKind)) return 'goods';
  return 'ignored';
}

function depositCash(inputs: JobInputs, gross: number): number {
  if (!inputs.hasDeposit) return 0;
  if (inputs.depositPercentage != null && Number.isFinite(inputs.depositPercentage)) {
    return round2(gross * inputs.depositPercentage);
  }
  return round2(inputs.depositAmount || 0);
}

function margin(part: number, whole: number): number | null {
  if (Math.abs(whole) < 0.005) return null;
  return part / whole;
}

function quoteScreenDayDivisor(inputs: JobInputs): number | null {
  const hasDays = inputs.projectDays != null && inputs.projectDays > 0;
  const hasHours = inputs.projectHours != null && inputs.projectHours > 0;
  if (!hasDays && !hasHours) return null;
  if (hasDays) return Math.ceil(inputs.projectDays as number);
  return 1;
}

function lineDayQty(line: JobLine): number {
  const unit = String(line.unit || '').trim().toLowerCase();
  if (/^(d|day|days)$/.test(unit)) return line.qty || 0;
  if (line.labourDays != null && line.labourDays > 0) return line.labourDays;
  return 0;
}

export function resolveKeystoneDays(inputs: JobInputs, overrides: Record<string, CostBucket> = {}): KeystoneDays {
  if (inputs.projectDays != null && inputs.projectDays > 0) {
    return { days: inputs.projectDays, source: 'Project duration stored on the quote' };
  }
  const fromFlatRate = round2(
    inputs.lines
      .filter((line) => analysisBucket(line, overrides) === 'company_flat_rate')
      .reduce((total, line) => total + lineDayQty(line), 0)
  );
  if (fromFlatRate > 0) {
    return { days: fromFlatRate, source: 'Company Flat Rate day quantity' };
  }
  return { days: null, source: 'Not recorded on this quote' };
}

/**
 * Frozen copy of today's quote totals.
 * Profit margin is a percentage of base cost only. The developer fee is always zero.
 * Admin is base × admin% , then capped.
 */
export function ps1Totals(inputs: JobInputs): Ps1Trace {
  const vatEnabled = inputs.vatEnabled;
  const vatPct = inputs.vatPct;
  const nets = inputs.lines.map((line) => ({
    line,
    net: lineNet(line, vatEnabled, vatPct),
  }));
  const goodsNet = sum(nets.filter((row) => ps1Bucket(row.line) === 'goods').map((row) => row.net));
  const labourRows = nets.filter((row) => ps1Bucket(row.line) === 'direct_labour');
  const labourNet = sum(labourRows.map((row) => row.net));
  const flatRateInsideLabour = sum(
    labourRows
      .filter((row) => defaultBucket(row.line) === 'company_flat_rate')
      .map((row) => row.net)
  );
  const baseCost = round2(goodsNet + labourNet);
  const adminUncapped = round2(baseCost * (inputs.adminPct || 0));
  const adminFee = round2(Math.min(adminUncapped, inputs.adminCap || 0));
  const contingency = round2(baseCost * (inputs.contingencyPct || 0));
  const profitMargin = round2(baseCost * (inputs.profitPct || 0));
  const preProfit = round2(baseCost + adminFee + contingency);
  const customerPriceExVat = round2(preProfit + profitMargin);
  const vatAmount = vatEnabled ? round2(customerPriceExVat * vatPct) : 0;
  const customerPriceIncVat = round2(customerPriceExVat + vatAmount);
  const quoteScreenProfit = round2(adminFee + contingency + profitMargin);
  const dayDivisor = quoteScreenDayDivisor(inputs);
  const quoteScreenProfitPerDay = dayDivisor == null
    ? null
    : round2(quoteScreenProfit / dayDivisor);

  return {
    goodsNet,
    labourNet,
    flatRateInsideLabour,
    baseCost,
    adminUncapped,
    adminFee,
    adminCapped: adminUncapped > (inputs.adminCap || 0) + 0.001,
    contingency,
    profitMargin,
    customerPriceExVat,
    vatAmount,
    customerPriceIncVat,
    quoteScreenProfit,
    quoteScreenDayDivisor: dayDivisor,
    quoteScreenProfitPerDay,
  };
}

function fundingFor(lines: JobLine[], inputs: JobInputs): number {
  return sum(lines.map((line) => lineSupplierCash(line, inputs.vatEnabled, inputs.vatPct)));
}

function finishResult(
  partial: Omit<PricingResult, 'grossMargin' | 'netJobMargin' | 'profitPerKeystoneDay' | 'customerDeposit' | 'fundingSurplus'>,
  inputs: JobInputs,
  keystoneDays: number | null
): PricingResult {
  const customerDeposit = depositCash(inputs, partial.customerPriceIncVat);
  const fundingSurplus = round2(customerDeposit - partial.supplierFundingRequirement);
  return {
    ...partial,
    grossMargin: margin(partial.grossProfit, partial.customerPriceExVat),
    netJobMargin: margin(partial.netJobProfit, partial.customerPriceExVat),
    profitPerKeystoneDay: keystoneDays && keystoneDays > 0
      ? round2(partial.netJobProfit / keystoneDays)
      : null,
    customerDeposit,
    fundingSurplus,
  };
}

function applyPs1(inputs: JobInputs, keystone: KeystoneDays): PricingResult {
  const trace = ps1Totals(inputs);
  const goodsLines = inputs.lines.filter((line) => ps1Bucket(line) === 'goods');
  const grossProfit = round2(trace.customerPriceExVat - trace.goodsNet - trace.labourNet);
  return finishResult({
    structureId: 'ps1',
    version: PS1_VERSION,
    label: 'PS1 — Enshrined / Legacy',
    customerPriceExVat: trace.customerPriceExVat,
    customerPriceIncVat: trace.customerPriceIncVat,
    goodsOutlay: trace.goodsNet,
    directLabour: trace.labourNet,
    companyOverhead: 0,
    contingency: trace.contingency,
    grossProfit,
    netJobProfit: grossProfit,
    keystoneDays: keystone.days,
    quoteScreenProfit: trace.quoteScreenProfit,
    quoteScreenProfitPerDay: trace.quoteScreenProfitPerDay,
    supplierFundingRequirement: fundingFor(goodsLines, inputs),
    notes: [
      'Prices every stored line by its line kind. Company Flat Rate is a labour line, so PS1 keeps it inside Direct Labour.',
      'There is no separate company-overhead cost. Admin, contingency and the profit margin are the profit figure the quote screen shows.',
      'Profit margin is a percentage of base cost only. Admin and contingency are not marked up again. The developer fee is ignored.',
      `${PS1_VERSION} is frozen. This reading does not change the quote.`,
    ],
  }, inputs, keystone.days);
}

function applyPs2(
  inputs: JobInputs,
  trace: Ps1Trace,
  keystone: KeystoneDays,
  overrides: Record<string, CostBucket>
): PricingResult {
  const goodsLines = inputs.lines.filter((line) => analysisBucket(line, overrides) === 'goods');
  const directLines = inputs.lines.filter((line) => {
    const bucket = analysisBucket(line, overrides);
    return bucket === 'direct_fitter' || bucket === 'additional_trade' || bucket === 'other_labour';
  });
  const flatLines = inputs.lines.filter((line) => analysisBucket(line, overrides) === 'company_flat_rate');
  const goodsOutlay = sum(goodsLines.map((line) => lineNet(line, inputs.vatEnabled, inputs.vatPct)));
  const directLabour = sum(directLines.map((line) => lineNet(line, inputs.vatEnabled, inputs.vatPct)));
  const flatRate = sum(flatLines.map((line) => lineNet(line, inputs.vatEnabled, inputs.vatPct)));
  const companyOverhead = round2(flatRate + trace.adminFee);
  const grossProfit = round2(trace.customerPriceExVat - goodsOutlay - directLabour);
  const netJobProfit = round2(grossProfit - companyOverhead - trace.contingency);
  return finishResult({
    structureId: 'ps2',
    version: PS2_VERSION,
    label: 'PS2 — Normalised Keystone Model',
    customerPriceExVat: trace.customerPriceExVat,
    customerPriceIncVat: trace.customerPriceIncVat,
    goodsOutlay,
    directLabour,
    companyOverhead,
    contingency: trace.contingency,
    grossProfit,
    netJobProfit,
    keystoneDays: keystone.days,
    quoteScreenProfit: null,
    quoteScreenProfitPerDay: null,
    supplierFundingRequirement: fundingFor(goodsLines, inputs),
    notes: [
      'Draft, and price-neutral. The customer price is the PS1 price. PS2 only moves the same money into the right buckets.',
      'Company Flat Rate is central company overhead, not direct labour. The admin fee sits with it. Contingency stays a reserve, deducted before net job profit.',
      'Net job profit equals the profit-margin line on the quote. James/Simon salary is not deducted — that treatment is still TBC.',
      'Reclassifying a line changes gross profit and overhead. It does not change this draft net, because the price stays the same.',
    ],
  }, inputs, keystone.days);
}

function applyPs3(
  inputs: JobInputs,
  ps2: PricingResult,
  mood: number
): PricingResult {
  const factor = Number.isFinite(mood) ? mood : 1;
  const adjustment = round2(ps2.netJobProfit * (factor - 1));
  const customerPriceExVat = round2(ps2.customerPriceExVat + adjustment);
  const vatAmount = inputs.vatEnabled ? round2(customerPriceExVat * inputs.vatPct) : 0;
  const customerPriceIncVat = round2(customerPriceExVat + vatAmount);
  const grossProfit = round2(customerPriceExVat - ps2.goodsOutlay - ps2.directLabour);
  const netJobProfit = round2(grossProfit - ps2.companyOverhead - ps2.contingency);
  return finishResult({
    structureId: 'ps3',
    version: PS3_VERSION,
    label: 'PS3 — Experimental / Market Model',
    customerPriceExVat,
    customerPriceIncVat,
    goodsOutlay: ps2.goodsOutlay,
    directLabour: ps2.directLabour,
    companyOverhead: ps2.companyOverhead,
    contingency: ps2.contingency,
    grossProfit,
    netJobProfit,
    keystoneDays: ps2.keystoneDays,
    quoteScreenProfit: null,
    quoteScreenProfitPerDay: null,
    supplierFundingRequirement: ps2.supplierFundingRequirement,
    notes: [
      'Experimental. A market/mood factor scales the required return, which is PS2 net job profit. Costs stay as in PS2.',
      'Mood 1.00 leaves the customer price identical to PS2. Above 1 asks the job to return more; below 1 accepts less.',
      'Nothing in this column is saved onto the quote.',
    ],
  }, inputs, ps2.keystoneDays);
}

export function compareStructures(
  inputs: JobInputs,
  overrides: Record<string, CostBucket> = {},
  mood = 1
): StructureComparison {
  const trace = ps1Totals(inputs);
  const keystone = resolveKeystoneDays(inputs, overrides);
  const ps1 = applyPs1(inputs, keystone);
  const ps2 = applyPs2(inputs, trace, keystone, overrides);
  const ps3 = applyPs3(inputs, ps2, mood);
  const lines = inputs.lines.map((line) => ({
    line,
    net: lineNet(line, inputs.vatEnabled, inputs.vatPct),
    ps1Bucket: ps1Bucket(line),
    analysisBucket: analysisBucket(line, overrides),
  }));
  return { ps1, ps2, ps3, trace, keystone, lines, mood: Number.isFinite(mood) ? mood : 1 };
}

const PENNY = 0.015;

function delta(actual: number, stored: number | null): number | null {
  if (stored == null || !Number.isFinite(stored)) return null;
  return round2(actual - stored);
}

export function checkReproduction(
  ps1: PricingResult,
  stored: StoredQuoteTotals,
  versionActive: boolean
): ReproductionCheck {
  const netDelta = delta(ps1.customerPriceExVat, stored.net);
  const grossDelta = delta(ps1.customerPriceIncVat, stored.gross);
  const depositDelta = delta(ps1.customerDeposit, stored.deposit);
  const comparable = [netDelta, grossDelta].filter((value): value is number => value != null);
  if (!versionActive) {
    return { status: 'inactive-version', netDelta, grossDelta, depositDelta };
  }
  if (comparable.length === 0) {
    return { status: 'no-stored-total', netDelta, grossDelta, depositDelta };
  }
  const matches = comparable.every((value) => Math.abs(value) < PENNY);
  return { status: matches ? 'match' : 'differs', netDelta, grossDelta, depositDelta };
}

export function formatMoney(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(value);
}

export function formatPercent(ratio: number | null | undefined): string {
  if (ratio == null || !Number.isFinite(ratio)) return '—';
  return `${(ratio * 100).toFixed(1)}%`;
}

export function formatDays(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  const rounded = Math.round(value * 100) / 100;
  return `${rounded} ${rounded === 1 ? 'day' : 'days'}`;
}

export function fundingLabel(surplus: number): string {
  if (Math.abs(surplus) < 0.005) return 'Covered';
  return surplus > 0 ? 'Surplus' : 'Shortfall';
}
